/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import {
  CaretDownOutlined,
  CaretRightOutlined,
  WarningFilled
} from '@ant-design/icons';
import { Transforms } from 'slate';
import { Divider, Switch, Tooltip } from 'antd';
import SubProcedureIcon from '@/components/icons/SubProcedureIcon';
import JsonViewer from '@/components/JsonViewer';
import moment from 'moment';
import { useApolloClient } from '@apollo/client';
import { ReactEditor, useSlate } from 'slate-react';
import { GET_LOG_MANAGER } from '@/graphql/cache/playground';
import DebugIcon from '@/components/icons/DebugIcon';
import InlinePlayIcon from '@/components/icons/InlinePlayIcon';
import InlinePauseIcon from '@/components/icons/InlinePauseIcon';
import { useSelector } from 'react-redux';
import { userSelector } from '@/stores/slices/user';
import { getWorkerLogLink } from '@/utils/internalTools';
import AppUtil from '@/utils/AppUtil';
import { trackEvent } from '@/tracker';
import { AnalyticsEvents } from '@/analytics/events';
import useFeatureFlag from '@/hooks/useFeatureFlag';
import useDepartment from '@/hooks/useDepartment';
import Gutter from '@/components/layout/Gutter';
import styles from './Editor.module.less';
import EditorLineResult from '../EditorLineResult/EditorLineResult';
import {
  getCollapsibleBlock,
  isLineHidden,
  isLineinCollapseRegion,
  isRequestExists
} from './editorUtil';
import {
  EditorBlock,
  EditorSourceType,
  ExecutionStatus,
  SentenceExecutionData,
  SentenceLanguage,
  SubDocument
} from './editorInterface';
import { TabKeys } from '../OutputPanel/OutputPanel';
import SubDocumentPopover from '../SubDocumentPopupover/SubDocumentPopover';
import WidgetManager from '../EditorWidgets/WidgetManager';
import { WIDGET_TYPES } from '../EditorWidgets/decorators';
import { useKogEditorContext } from './EditorProvider';
import EditorLineDropdown from './EditorLineDropdown';
import EditorLineRequestInfo from '../EditorLineRequestInfo/EditorLineRequestInfo';
import { resetEditorLines } from '../RichTextEditor/utils';
import WorkerStateUtil from '../../../../utils/WorkerStateUtil';
import { WorkerState } from '../WorkerStatusInfo/interface';
import { getSentenceMethodLearningWorker } from '../../../../utils/request';
import { MethodLearningState } from '../../../../components/MiniPlayground/interface';

function EditorLine(props: any) {
  const {
    workerId,
    leaf,
    blocks,
    children,
    activeBlock,
    showLineResult = true,
    isDebugMode,
    handleWidgetEdit,
    activeDocumentToken,
    sentenceForkedWorkers
  } = props;
  const { username } = useSelector(userSelector);

  const elementRef = useRef<HTMLDivElement | null>(null);

  const [isSubprocedurePopupVisible, setSubprocedurePopupVisible] =
    useState(false);
  const editor = useSlate();
  const {
    source,
    collapsedCodeBlocks,
    editorErrors,
    actions,
    widgets,
    startDebugLine
  } = useKogEditorContext();
  const [showDebugData, setShowDebugData] = useState(false);
  const [collapsedBlockLeftPx, setCollapsedBlockLeftPx] = useState(0);
  const [debugPointVisible, setDebugPointVisible] = useState(false);
  const [lineNumberHovered, setLineNumberHovered] = useState(false);

  const { department } = useDepartment();

  const [{ rolloutRestartPauseWorker }] = useFeatureFlag();
  const [{ rolloutKompileBrain }] = useFeatureFlag({
    context: { department: { key: department.id, dept_id: department.id } }
  });

  const isPlayground = source === EditorSourceType.PLAYGROUND;

  const textRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isDebugMode) {
      setShowDebugData(false);
    }
  }, [isDebugMode]);

  const client = useApolloClient();
  const logs = client.readQuery({ query: GET_LOG_MANAGER })?.getLogManager
    ?.data;

  const getExecutionStatusStyle = (status: ExecutionStatus | undefined) => {
    switch (status) {
      case ExecutionStatus.SUCCESS:
        return styles.success;
      case ExecutionStatus.ERROR:
        return `${styles.error} editorErrorLine`;
      case ExecutionStatus.RUNNING:
        return styles.running;
      case ExecutionStatus.PAUSED:
        return styles.warning;
      default:
        return '';
    }
  };

  const getEditorLeftLines = (depth: number) => {
    const items: JSX.Element[] = [];
    for (let i = 0; i <= depth; i += 1) {
      items.push(
        <div
          contentEditable={false}
          key={`editorLeftLine-${i}`}
          className={styles.editorRightLine}
          style={{ left: `-${35 * i}px`, userSelect: 'none' }}
        />
      );
    }
    return items;
  };

  const { uiLineId, status, language } = leaf;
  const lineDepth = leaf.tabCount;
  const lineNumber = blocks.findIndex(
    (b: EditorBlock) => b.children[0].uiLineId === uiLineId
  );

  const lineHasError = editorErrors?.[lineNumber];

  const isWidgetOpen = widgets.data?.lineId === leaf.uiLineId;

  const isMiniPlayground = source === EditorSourceType.MINI_PLAYGROUND;

  const isSentenceWrapper =
    leaf.text.startsWith('if ') || leaf.text.startsWith('process ');

  const sentenceMethodLearningWorker = getSentenceMethodLearningWorker(
    sentenceForkedWorkers,
    leaf
  );

  const methodLearningLoading = sentenceMethodLearningWorker
    ? WorkerStateUtil.getWorkerStateLifecycle(
        sentenceMethodLearningWorker.internalState as WorkerState
      )[MethodLearningState.LOADING]
    : false;

  const handleLineClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isMiniPlayground && isRequestExists(leaf)) {
      props.setActiveBlock(leaf, TabKeys.Requests);
    }
  };

  useEffect(() => {
    if (!isMiniPlayground && isRequestExists(leaf)) {
      props.setActiveBlock(leaf, TabKeys.Requests);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, leaf.requests, leaf.editorRequests]);

  useEffect(() => {
    if (textRef.current) {
      const leafNode = textRef.current.querySelector('[data-slate-leaf]');
      if (leafNode) {
        const leafNodeRect = leafNode.getBoundingClientRect();
        const collapsedBlockLeftPx = leafNodeRect.width + 10;
        setCollapsedBlockLeftPx(collapsedBlockLeftPx);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textRef.current, leaf?.text]);

  const isSentenceFixedByAutopilot = !!leaf.originalText;

  const handleProcedureTokenClick = (token: SubDocument) => {
    props.onSubprocedureClick(leaf, token);
    actions.updateActiveBlock(null);
  };

  const getExecutionData = (executionData: SentenceExecutionData) => {
    if (executionData?.startedAt && executionData?.completedAt) {
      const diff = moment(executionData.completedAt).diff(
        moment(executionData.startedAt)
      );
      return {
        ...executionData,
        timeToComplete: diff ? `${diff} ms` : undefined
      };
    }

    return executionData;
  };

  const logLink = getWorkerLogLink(
    workerId,
    moment(
      getExecutionData(logs?.[`${leaf.uiLineId}-execution`])?.startedAt
    ).subtract(100, 'millisecond'),
    moment(
      getExecutionData(logs?.[`${leaf.uiLineId}-execution`])?.completedAt
    ).add(100, 'millisecond')
  );

  const handleWidgetUpdate = (
    key: string,
    value: string,
    type: WIDGET_TYPES
  ) => {
    handleWidgetEdit({
      [key]: {
        value,
        type
      }
    });
  };

  const collapsibleBlock = getCollapsibleBlock(collapsedCodeBlocks, lineNumber);

  const handleCollapse = (e: React.MouseEvent) => {
    e.preventDefault();
    ReactEditor.focus(editor);
    const { selection } = editor;
    const currentSelectedLine = selection?.anchor?.path[0];
    const isInCollapsibleRegion = isLineinCollapseRegion(
      collapsedCodeBlocks,
      currentSelectedLine,
      lineNumber
    );
    const textLengthAtLine = blocks[lineNumber].children[0].text.length;
    if (isInCollapsibleRegion) {
      Transforms.select(editor, {
        anchor: {
          path: [lineNumber, 0],
          offset: textLengthAtLine
        },
        focus: {
          path: [lineNumber, 0],
          offset: textLengthAtLine
        }
      });
    }
    actions.toggleFold(lineNumber);
  };

  const getLineNumberPadding = () => {
    if (collapsibleBlock) {
      return 0;
    }
    return isMiniPlayground ? 10 : 20;
  };

  const handlePauseMarkClick = () => {
    trackEvent(AnalyticsEvents.PLAYGROUND.PGPauseAtLineButton, {
      playgroundId: workerId,
      lineNumber: lineNumber + 1,
      state: leaf.shouldPause ? 'off' : 'on'
    });
    Transforms.setNodes(
      editor,
      { shouldPause: !leaf.shouldPause },
      { at: [lineNumber, 0] }
    );
    setDebugPointVisible(false);
    // On pause, reset the editor line below the paused line
    actions.setStartDebugLine(null);
    resetEditorLines(editor, null);
  };

  const handleStartMarkerClick = () => {
    trackEvent(AnalyticsEvents.PLAYGROUND.PGStartAtLineButton, {
      playgroundId: workerId,
      lineNumber: lineNumber + 1
    });
    if (startDebugLine?.uiLineId === leaf.uiLineId) {
      actions.setStartDebugLine(null);
    } else {
      // Send the leaf, in this case as the startDebugLine may not be updated yet
      resetEditorLines(editor, leaf);
      actions.setStartDebugLine(leaf);
    }
    setDebugPointVisible(false);
  };

  const handleMouseLeave = () => {
    setLineNumberHovered(false);
    if (!leaf?.shouldPause && leaf.uiLineId !== startDebugLine?.uiLineId) {
      setDebugPointVisible(false);
    }
  };

  /**
   * KOG-8611 TEMPORARILY ADDED FOR DEBUGGING PURPOSE IN THE SCOPE OF FLEX GRAMMAR
   * Updates the language of the line to "english" or "flex english"
   * when copilot switch is toggled
   * Note: Used only in case of flex english and hidden by rolloutKompileBrain flag
   * @param checked - boolean value of the copilot switch
   */
  const handleCopilotToggle = (checked: boolean) => {
    Transforms.setNodes(
      editor,
      {
        flexDisabled: !checked,
        language: checked
          ? SentenceLanguage.FlexEnglish
          : SentenceLanguage.English
      },
      { at: [lineNumber, 0] }
    );
  };

  return (
    <div
      ref={elementRef}
      className={classNames(
        styles.editorLine,
        styles[`editorLine-${source}`],
        getExecutionStatusStyle(status),
        activeBlock?.uiLineId === uiLineId ? styles.active : '',
        lineHasError ? styles.lineError : '',
        activeBlock?.uiLineId === uiLineId &&
          source !== EditorSourceType.MINI_PLAYGROUND
          ? styles.active
          : '',
        leaf.shouldPause ? styles.pauseMarker : '',
        {
          [styles.isFlex]:
            rolloutKompileBrain && language === SentenceLanguage.FlexEnglish,
          [styles.isGeneratedKlang]:
            rolloutKompileBrain && language === SentenceLanguage.English
        }
      )}
      style={{
        marginLeft: `${35 * lineDepth + (isMiniPlayground ? 40 : 70)}px`,
        display: isLineHidden(collapsedCodeBlocks, lineNumber)
          ? 'none'
          : 'block'
      }}
      onClick={(e) => handleLineClick(e)}
      key={`editorLine-${uiLineId}`}
      data-cy={`editorLine-${uiLineId}`}
    >
      {/* left spaces */}
      {getEditorLeftLines(lineDepth)}

      {/* line number */}
      <div
        data-cy={`line-number-${leaf.uiLineId}`}
        onContextMenu={(e) => {
          if (isDebugMode) {
            e.preventDefault();
            setShowDebugData((prev) => !prev);
          }
        }}
        onMouseEnter={() => {
          setLineNumberHovered(true);
        }}
        onMouseLeave={() => {
          handleMouseLeave();
        }}
        className={styles.editorLineNumber}
        contentEditable={false}
        style={{
          left: `-${35 * lineDepth + 25}px`,
          userSelect: 'none',
          paddingRight: getLineNumberPadding()
        }}
      >
        {rolloutRestartPauseWorker &&
          AppUtil.isSupportUser(username) &&
          isPlayground &&
          !lineHasError &&
          lineNumberHovered && (
            <>
              {/* TODO: Feature flag is added, need to check if feature flag need to be department basis or not. Also it is currently visible to our Support user only  */}
              {debugPointVisible ? (
                <span
                  className={classNames(
                    styles.markerPosition,
                    styles.markContainer
                  )}
                >
                  {/* TODO: Show start play, only if it is rerun, for new script start from line number doesn't make sense  */}
                  <span
                    data-cy={`start-${leaf.uiLineId}`}
                    className={styles.startPlay}
                    onClick={handleStartMarkerClick}
                    title="Click to start from this line"
                  >
                    <InlinePlayIcon />
                  </span>
                  {/* TODO: Check if pause line number > start line number  */}
                  <span
                    data-cy={`pause-${leaf.uiLineId}`}
                    className={styles.pause}
                    onClick={handlePauseMarkClick}
                    title="Click to pause at this line"
                  >
                    <InlinePauseIcon />
                  </span>
                </span>
              ) : (
                !leaf.shouldPause &&
                startDebugLine?.uiLineId !== leaf.uiLineId && (
                  <span
                    data-cy={`debug-${leaf.uiLineId}`}
                    className={classNames(
                      styles.markerPosition,
                      styles.debugMark
                    )}
                    onClick={() => setDebugPointVisible(true)}
                    title="Click to debug this line"
                  >
                    <DebugIcon />
                  </span>
                )
              )}
            </>
          )}

        <span
          data-cy={`pause-marker-${leaf.uiLineId}`}
          className={classNames(
            styles.markerPosition,
            styles.mark,
            styles.pauseMark,
            {
              [styles.markerVisible]: leaf?.shouldPause
            }
          )}
          onClick={handlePauseMarkClick}
        />

        <span
          data-cy={`start-marker-${leaf.uiLineId}`}
          className={classNames(
            styles.markerPosition,
            styles.mark,
            styles.startMark,
            {
              [styles.markerVisible]: startDebugLine?.uiLineId === leaf.uiLineId
            }
          )}
          onClick={handleStartMarkerClick}
        />

        {lineNumber + 1}

        {lineHasError && (
          <div className={styles.lineErrorIcon}>
            <Tooltip title={lineHasError} trigger="hover" placement="right">
              <WarningFilled />
            </Tooltip>
          </div>
        )}

        {collapsibleBlock && (
          <div className={styles.foldIcon} onClick={handleCollapse}>
            {collapsibleBlock?.isCollapsed ? (
              <CaretRightOutlined />
            ) : (
              <CaretDownOutlined />
            )}
          </div>
        )}
      </div>

      {/* content */}
      <div
        className={classNames(
          styles.editorLineDetails,
          'justify-space-between'
          // {
          //   'justify-space-between': !leaf?.requests?.length
          // }
        )}
      >
        {/* {!leaf.answer && leaf.requests?.length > 0 && !isMiniPlayground ? (
          <div
            className={styles.exceptionMarker}
            onClick={(e) => {
              e.stopPropagation();
              props.setActiveBlock(leaf, TabKeys.Requests);
            }}
          >
            <QuestionCircleFilled
              style={{ height: '18px', width: '18px', color: '#E9B825' }}
            />
          </div>
        ) : null} */}

        {/* middle text */}
        <div
          ref={textRef}
          data-lineid={uiLineId}
          data-linenumber={lineNumber}
          className={classNames(
            styles.editorLineInput,
            {
              shimmer:
                status === ExecutionStatus.RUNNING || methodLearningLoading
            },
            { 'shimmer-finite': isSentenceFixedByAutopilot }
          )}
        >
          {/* {leaf.requests && (
            <div
              className={styles.exceptionMarker}
              onClick={(e) => {
                e.stopPropagation();
                props.setActiveBlock(leaf, TabKeys.Requests);
              }}
            >
              <WarningFilled
                style={{ height: '18px', width: '18px', color: '#cf2a38' }}
              />
            </div>
          )} */}
          {children}
          {collapsibleBlock?.isCollapsed ? (
            <div
              className={styles.collapsedBlock}
              style={{ left: `${collapsedBlockLeftPx}px` }}
              onClick={handleCollapse}
            />
          ) : null}

          {/* this is where we can show sentence execution data */}
          {showDebugData && (
            <div
              contentEditable={false}
              style={{ userSelect: 'none' }}
              data-cy="line-debugger"
            >
              <Divider />
              <a href={logLink} target="_blank" rel="noreferrer">
                click here to open logs
              </a>
              <Divider />
              <JsonViewer
                data={logs[`${leaf.uiLineId}-line`]}
                name="workerDocumentLine"
              />
              <Divider />
              <JsonViewer
                data={getExecutionData(logs[`${leaf.uiLineId}-execution`])}
                name="sentenceExecution"
              />
            </div>
          )}

          {isMiniPlayground ? <EditorLineRequestInfo sentence={leaf} /> : null}

          {isWidgetOpen && (
            <div
              contentEditable={false}
              style={{ userSelect: 'none' }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {widgets.data && (
                <WidgetManager
                  widgetKey={widgets.data.widgetKey}
                  widgetValue={widgets.data.widgetValue}
                  widgetType={widgets.data.widgetType}
                  onUpdate={handleWidgetUpdate}
                />
              )}
            </div>
          )}
        </div>

        {/* right icons */}
        <div className={styles.editorLineActions} contentEditable={false}>
          {
            // KOG-8611 TEMPORARILY ADDED FOR DEBUGGING PURPOSE IN THE SCOPE OF FLEX GRAMMAR
            rolloutKompileBrain &&
              isSentenceWrapper &&
              leaf.flexDisabled !== undefined && (
                <>
                  <Tooltip title="Switch on/off copilot for this line. Note: when the copilot is enabled, it may replace the child lines">
                    <Switch
                      checkedChildren="Copilot"
                      unCheckedChildren="Copilot"
                      defaultChecked={!leaf.flexDisabled}
                      onChange={handleCopilotToggle}
                    />
                  </Tooltip>
                  <Gutter size={1} />
                </>
              )
          }

          {leaf.subDocuments && (
            <>
              <Tooltip title="View subprocess" color="#323232">
                <div
                  className={classNames([
                    styles.autoPilotSVGContainer,
                    styles.autopilotScaleAnimation
                  ])}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setSubprocedurePopupVisible(!isSubprocedurePopupVisible);
                  }}
                  data-cy="subprocess-icon"
                >
                  <SubProcedureIcon width={16} height={16} fill="#9747FF" />
                </div>
              </Tooltip>
              <SubDocumentPopover
                documentTokens={
                  leaf.editorSubDocuments
                    ? leaf.editorSubDocuments[leaf.token!]
                    : leaf.subDocuments
                }
                visible={isSubprocedurePopupVisible}
                hidePopover={() => setSubprocedurePopupVisible(false)}
                onProcedureClick={handleProcedureTokenClick}
              />
            </>
          )}
          {showLineResult && (
            <EditorLineResult
              workerId={workerId}
              sentence={leaf}
              onAnswerClick={(...data) => {
                props.setActiveBlock(...data);
                actions.updateEditorStatus({ showOutputPanel: true });
              }}
              activeDocumentToken={activeDocumentToken}
              source={source}
            />
          )}
          {workerId && (
            <EditorLineDropdown
              leaf={leaf}
              activeDocumentToken={activeDocumentToken}
              lineNumber={lineNumber}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function RenderElement(props: any) {
  const {
    workerId,
    attributes,
    children,
    element,
    blocks,
    activeBlock,
    autoPilotChecked,
    setActiveBlock,
    showLineResult,
    onSubprocedureClick,
    isDebugMode,
    handleWidgetEdit,
    replacerObj,
    activeDocumentToken,
    sentenceForkedWorkers
  } = props;

  switch (element?.type) {
    case 'EditorLine':
    default:
      return (
        <div {...attributes} id={element.children[0].uiLineId}>
          <EditorLine
            workerId={workerId}
            leaf={element.children[0]}
            blocks={blocks}
            activeBlock={activeBlock}
            autoPilotChecked={autoPilotChecked}
            setActiveBlock={setActiveBlock}
            showLineResult={showLineResult}
            onSubprocedureClick={onSubprocedureClick}
            isDebugMode={isDebugMode}
            handleWidgetEdit={handleWidgetEdit}
            replacerObj={replacerObj}
            activeDocumentToken={activeDocumentToken}
            sentenceForkedWorkers={sentenceForkedWorkers}
          >
            {children}
          </EditorLine>
        </div>
      );
  }
}

export default RenderElement;
